import { api } from './api';
import { impersonationStore } from '../store/impersonation';

export const subscriptionService = {
    async getSubscriptionStatus(impersonateId = null) {
        // Add impersonateId as a query parameter if provided
        const url = impersonateId ? `/subscriptions?impersonateId=${impersonateId}` : '/subscriptions';
        const response = await api.get(url);
        return response;
    },

    async getModeratorSubscriptions() {
        const response = await api.get('/subscriptions/moderator-subscriptions');
        return response;
    },

    async verifyTwitchName(twitchName) {
        const response = await api.post('/subscriptions/verify-twitch', { twitchName });
        return response;
    },

    async createSubscription(data) {
        const {
            twitchName,
            months,
            donorName,
            paymentDetails,
            plan
        } = data;

        const payload = {
            twitchName,
            months,
            donorName,
            plan,
            payment: {
                orderId: paymentDetails.orderId,
                payerEmail: paymentDetails.payerEmail,
                payerName: paymentDetails.payerName,
                amount: paymentDetails.amount,
                currency: paymentDetails.currency,
                status: paymentDetails.status,
                create_time: paymentDetails.createTime,
                update_time: paymentDetails.updateTime
            }
        };

        const response = await api.post('/subscriptions/activate', payload);
        return response;
    },

    async getUpgradeQuote(impersonateId = null) {
        return api.get('/stripe/upgrade-quote', { params: impersonateId ? { impersonateId } : {} });
    },

    // option: 'difference' | 'convert' | 'recurring'
    async upgradePlan(option, impersonateId = null) {
        return api.post('/stripe/upgrade', { option }, { params: impersonateId ? { impersonateId } : {} });
    },

    async downgradePlan(impersonateId = null) {
        return api.post('/stripe/downgrade', null, { params: impersonateId ? { impersonateId } : {} });
    },

    async getSubscriptionHistory() {
        const response = await api.get('/subscriptions/history');
        return response;
    },

    async cancelSubscription(subscriptionId) {
        const response = await api.post(`/subscriptions/${subscriptionId}/cancel`);
        return response;
    },

    async extendSubscription(subscriptionId, months) {
        const response = await api.post(`/subscriptions/${subscriptionId}/extend`, { months });
        return response;
    },

    async transferSubscription(subscriptionId, newTwitchName) {
        const response = await api.post(`/subscriptions/${subscriptionId}/transfer`, { newTwitchName });
        return response;
    },

    async updateModule(moduleData) {
        // Récupérer l'ID d'impersonation s'il existe
        const impersonateId = impersonationStore.getImpersonatedId();
        
        // Ajouter l'ID d'impersonation à l'URL si nécessaire
        const url = impersonateId ? `/subscriptions/modules?impersonateId=${impersonateId}` : '/subscriptions/modules';
        
        const response = await api.post(url, moduleData);
        return response;
    },

    async updateAIMemory(memory) {
        // Récupérer l'ID d'impersonation s'il existe
        const impersonateId = impersonationStore.getImpersonatedId();

        // Ajouter l'ID d'impersonation à l'URL si nécessaire
        const url = impersonateId ? `/subscriptions/ai-memory?impersonateId=${impersonateId}` : '/subscriptions/ai-memory';

        const response = await api.post(url, { memory });
        return response;
    },

    async updateAccountStatus(isActive) {
        // Récupérer l'ID d'impersonation s'il existe
        const impersonateId = impersonationStore.getImpersonatedId();
        
        // Ajouter l'ID d'impersonation à l'URL si nécessaire
        const url = impersonateId ? `/subscriptions/status?impersonateId=${impersonateId}` : '/subscriptions/status';
        
        const response = await api.post(url, { isActive });
        return response;
    },

    async createCustomBotOAuthLink(redirectUri) {
        const impersonateId = impersonationStore.getImpersonatedId();
        const url = impersonateId
            ? `/subscriptions/custom-bot/oauth-link?impersonateId=${impersonateId}`
            : '/subscriptions/custom-bot/oauth-link';

        const response = await api.post(url, { redirectUri });
        return response;
    },

    async completeCustomBotOAuth(code, state, redirectUri) {
        const response = await api.post('/subscriptions/custom-bot/complete', {
            code,
            state,
            redirectUri
        });
        return response;
    },

    async updateCustomBotStatus(isEnabled) {
        const impersonateId = impersonationStore.getImpersonatedId();
        const url = impersonateId
            ? `/subscriptions/custom-bot/status?impersonateId=${impersonateId}`
            : '/subscriptions/custom-bot/status';

        const response = await api.post(url, { isEnabled });
        return response;
    },

    async updateReportSettings(sendPeriodicReportToDiscord) {
        const impersonateId = impersonationStore.getImpersonatedId();
        const url = impersonateId
            ? `/subscriptions/reports/settings?impersonateId=${impersonateId}`
            : '/subscriptions/reports/settings';

        const response = await api.post(url, { sendPeriodicReportToDiscord });
        return response;
    },

    async getReports(params = {}) {
        const impersonateId = impersonationStore.getImpersonatedId();
        const query = new URLSearchParams();
        if (params.category) query.set('category', params.category);
        if (params.streamId) query.set('streamId', params.streamId)
        if (params.fromUtc) query.set('fromUtc', params.fromUtc);
        if (params.toUtc) query.set('toUtc', params.toUtc);
        if (params.take) query.set('take', params.take);
        if (params.skip) query.set('skip', params.skip);
        if (impersonateId) query.set('impersonateId', impersonateId);

        const queryString = query.toString();
        const url = queryString ? `/subscriptions/reports?${queryString}` : '/subscriptions/reports';

        const response = await api.get(url);
        return response;
    },

    async getReportSessions(params = {}) {
        const impersonateId = impersonationStore.getImpersonatedId();
        const query = new URLSearchParams();
        if (params.category) query.set('category', params.category);
        if (params.take) query.set('take', params.take);
        if (impersonateId) query.set('impersonateId', impersonateId);

        const queryString = query.toString();
        const url = queryString ? `/subscriptions/reports/sessions?${queryString}` : '/subscriptions/reports/sessions';

        const response = await api.get(url);
        return response;
    },

    async checkRolePlayPerson(name) {
        const impersonateId = impersonationStore.getImpersonatedId();
        const encodedName = encodeURIComponent(name || '');
        const url = impersonateId
            ? `/subscriptions/roleplay/check?name=${encodedName}&impersonateId=${impersonateId}`
            : `/subscriptions/roleplay/check?name=${encodedName}`;

        const response = await api.get(url);
        return response;
    },

    async searchRolePlayPersons(term) {
        const impersonateId = impersonationStore.getImpersonatedId();
        const encodedTerm = encodeURIComponent(term || '');
        const url = impersonateId
            ? `/subscriptions/roleplay/search?term=${encodedTerm}&impersonateId=${impersonateId}`
            : `/subscriptions/roleplay/search?term=${encodedTerm}`;

        const response = await api.get(url);
        return response;
    },

    async updateRolePlayPerson(payload) {
        const impersonateId = impersonationStore.getImpersonatedId();
        const url = impersonateId
            ? `/subscriptions/roleplay/check/update?impersonateId=${impersonateId}`
            : '/subscriptions/roleplay/check/update';

        const response = await api.post(url, payload);
        return response;
    },

    async createRolePlayPerson(payload) {
        const impersonateId = impersonationStore.getImpersonatedId();
        const url = impersonateId
            ? `/subscriptions/roleplay/create?impersonateId=${impersonateId}`
            : '/subscriptions/roleplay/create';

        const response = await api.post(url, payload);
        return response;
    }
};
